import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import type { ILocalAudioTrack, ILocalVideoTrack } from 'agora-rtc-sdk-ng';
import { ErrorCallback } from '../../types';
import type { AgoraRoomLike } from '../../types';
import type { BackgroundSettings } from './useBackgroundSettings/useBackgroundSettings';
import { SelectedParticipantProvider } from './useSelectedParticipant/useSelectedParticipant';

import AttachVisibilityHandler from './AttachVisibilityHandler/AttachVisibilityHandler';
import useBackgroundSettings from './useBackgroundSettings/useBackgroundSettings';
import { useAppState } from '../../state';
import useVideoCallChannelSession from '../../hooks/useVideoCallChannelSession/useVideoCallChannelSession';
import useHandleRoomDisconnection from './useHandleRoomDisconnection/useHandleRoomDisconnection';
import useHandleTrackPublicationFailed from './useHandleTrackPublicationFailed/useHandleTrackPublicationFailed';
import useLocalTracks from './useLocalTracks/useLocalTracks';
import useRestartAudioTrackOnDeviceChange from './useRestartAudioTrackOnDeviceChange/useRestartAudioTrackOnDeviceChange';
import useRoom from './useRoom/useRoom';
import useScreenShareToggle from './useScreenShareToggle/useScreenShareToggle';

export interface IVideoContext {
  room: AgoraRoomLike | null;
  localTracks: (ILocalAudioTrack | ILocalVideoTrack)[];
  isConnecting: boolean;
  connect: (token: string, channelName: string, uid: number, displayName: string) => void | Promise<void>;
  onError: ErrorCallback;
  getLocalVideoTrack: (newOptions?: unknown) => Promise<ILocalVideoTrack>;
  isAcquiringLocalTracks: boolean;
  removeLocalVideoTrack: () => void;
  removeLocalScreenTrack: () => void;
  isSharingScreen: boolean;
  toggleScreenShare: () => void;
  getAudioAndVideoTracks: () => Promise<void>;
  isBackgroundSelectionOpen: boolean;
  setIsBackgroundSelectionOpen: (value: boolean) => void;
  backgroundSettings: BackgroundSettings;
  setBackgroundSettings: (settings: BackgroundSettings) => void;
}

export const VideoContext = createContext<IVideoContext>(null!);

interface VideoProviderProps {
  options?: unknown;
  onError: ErrorCallback;
  children: ReactNode;
}

export function VideoProvider({ options, children, onError = () => {} }: VideoProviderProps) {
  const { resetRecording } = useAppState();
  const onErrorCallback: ErrorCallback = useCallback(
    error => {
      console.log(`ERROR: ${error.message}`, error);
      onError(error);
    },
    [onError]
  );

  const {
    localTracks,
    getLocalVideoTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    setLocalScreenTrack,
    removeLocalScreenTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks();
  const { room, isConnecting, connect } = useRoom(localTracks, onErrorCallback, options);

  useEffect(() => {
    if (!room) resetRecording();
  }, [room, resetRecording]);

  useEffect(() => {
    if (!room) return;
    try {
      sessionStorage.setItem('hui_video_call_page_href', window.location.href);
    } catch {
      /* ignore */
    }
  }, [room]);
  const cameraVideoTrack = localTracks.find(
    t => t.trackMediaType === 'video' && !(t as any).trackName?.includes?.('screen')
  ) as ILocalVideoTrack | undefined;
  const [isSharingScreen, toggleScreenShare] = useScreenShareToggle(
    room,
    cameraVideoTrack,
    onError,
    setLocalScreenTrack,
    removeLocalScreenTrack
  );

  useVideoCallChannelSession(room);

  useHandleRoomDisconnection(
    room,
    onError,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare
  );
  useHandleTrackPublicationFailed(room, onError);
  useRestartAudioTrackOnDeviceChange(localTracks);

  const [isBackgroundSelectionOpen, setIsBackgroundSelectionOpen] = useState(false);
  const videoTrack = localTracks.find(
    t => t.trackMediaType === 'video' && !(t as any).trackName?.includes?.('screen')
  ) as ILocalVideoTrack | undefined;
  const [backgroundSettings, setBackgroundSettings] = useBackgroundSettings(videoTrack, room);

  return (
    <VideoContext.Provider
      value={{
        room,
        localTracks,
        isConnecting,
        connect,
        onError: onErrorCallback,
        getLocalVideoTrack,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        removeLocalScreenTrack,
        isSharingScreen,
        toggleScreenShare,
        getAudioAndVideoTracks,
        isBackgroundSelectionOpen,
        setIsBackgroundSelectionOpen,
        backgroundSettings,
        setBackgroundSettings,
      }}
    >
      <SelectedParticipantProvider room={room}>{children}</SelectedParticipantProvider>
      <AttachVisibilityHandler />
    </VideoContext.Provider>
  );
}
